import { AlertCircle, CarFront, Globe, IdCard } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CitySelect } from "../city-select";
import { CountrySelect } from "../country-select";
import { DatePicker } from "../date-picker";
import type { GuestDataProps } from "../GuestForm";
import LoadingItem from "../LoadingItem";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from "../ui/alert";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Item, ItemContent, ItemMedia, ItemTitle } from "../ui/item";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Spinner } from "../ui/spinner";

export default function DocumentVerification({
  data,
  loading,
  onChange,
  onError,
  onNext,
}: {
  data: GuestDataProps;
  loading: boolean;
  onChange: (data: GuestDataProps) => void;
  onError?: (errors: string[]) => void;
  onNext?: () => void;
}) {
  const { t } = useTranslation();
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleChange = (field: keyof GuestDataProps, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const getErrorMessage = (errorCode: string): string => {
    return t(
      `documentVerification.errors.${errorCode}`,
      t("documentVerification.errors.required_field"),
    );
  };

  const hasError = (errorCode: string): boolean => {
    return errors.includes(errorCode);
  };
  useEffect(() => {
    const errors: string[] = [];
    if (!data?.documentNumber || data.documentNumber.trim() === "") {
      errors.push("required_document_number");
    }
    if (!data?.issuingDate) {
      errors.push("required_issuing_date");
    }
    if (data?.issuingDate && data?.issuingDate > new Date()) {
      errors.push("invalid_issuing_date");
    }
    if (!data?.expirationDate) {
      errors.push("required_expiration_date");
    }
    if (data?.expirationDate && data?.expirationDate <= new Date()) {
      errors.push("invalid_expiration_date");
    }
    if (!data?.issuingAuthority || data.issuingAuthority.trim() === "") {
      errors.push("required_issuing_authority");
    }
    if (!data?.issuingCountry || data.issuingCountry?.iso2?.trim() === "") {
      errors.push("required_issuing_country");
    }
    if (
      data.issuingCountry?.iso2 === "IT" &&
      (!data?.issuingCity ||
        !data.issuingCity.name ||
        data.issuingCity.name.trim() === "")
    ) {
      errors.push("required_issuing_city");
    }
    if (!data?.documentType || data.documentType.trim() === "") {
      errors.push("required_document_type");
    }
    if (
      !europeanCountriesIso2.includes(data.issuingCountry?.iso2 || "") &&
      (data.documentType == "ID_CARD" || data.documentType == "DRIVER_LICENSE")
    ) {
      errors.push("invalid_document_type");
      handleChange("documentType", null);
    }
    setErrors(errors);
    onError?.(errors);
    setHasErrors(errors.length > 0);
  }, [data]);

  const europeanCountriesIso2 = [
    "AT",
    "BE",
    "BG",
    "CY",
    "CZ",
    "DE",
    "DK",
    "EE",
    "ES",
    "FI",
    "FR",
    "GR",
    "HR",
    "HU",
    "IE",
    "IT",
    "LT",
    "LU",
    "LV",
    "MT",
    "NL",
    "PL",
    "PT",
    "RO",
    "SE",
    "SI",
    "SK",
  ];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data?.isDocumentExpired && !data?.expirationDate && (
          <div className="col-span-2">
            <Alert className="bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-200">
              <AlertIcon>
                <AlertCircle className="size-4 text-amber-600" />
              </AlertIcon>
              <div>
                <AlertTitle className="text-xs sm:text-sm font-semibold">
                  {t(
                    "documentTab.docExpiredWarningTitle",
                    "Attenzione: Documento scaduto",
                  )}
                </AlertTitle>
                <AlertDescription className="text-xs text-amber-800 dark:text-amber-300">
                  {t(
                    "documentTab.docExpiredWarningDesc",
                    "La data di scadenza del documento scansionato è superata. Il campo è stato lasciato vuoto: inserire un documento valido.",
                  )}
                </AlertDescription>
              </div>
            </Alert>
          </div>
        )}
        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          {!loading && data.frontDocumentData && (
            <img
              className="rounded-md"
              src={
                data.frontDocumentData
                  ? URL.createObjectURL(data.frontDocumentData)
                  : ""
              }
              alt="Front Document"
            />
          )}
        </div>
        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          {!loading && data.backDocumentData && (
            <img
              className="rounded-md"
              src={
                data.backDocumentData
                  ? URL.createObjectURL(data.backDocumentData)
                  : ""
              }
              alt="Front Document"
            />
          )}
        </div>

        {loading && (
          <div className="col-span-2 [--radius:1rem] mb-4">
            <Item variant="muted">
              <ItemMedia>
                <Spinner />
              </ItemMedia>
              <ItemContent>
                <ItemTitle className="line-clamp-1 text-xl font-bold">
                  <LoadingItem
                    text={t("loading_item.extraction_in_progress")}
                  />
                </ItemTitle>
              </ItemContent>
            </Item>
          </div>
        )}
        {!loading && (
          <>
            <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
              <Label>{t("documentVerification.documentNumber")}</Label>
              <Input
                disabled={loading}
                value={data?.documentNumber}
                onChange={(e) => handleChange("documentNumber", e.target.value)}
                placeholder={t(
                  "documentVerification.placeholders.documentNumber",
                )}
                className={
                  hasError("required_document_number") ? "border-red-500" : ""
                }
              />
              {hasError("required_document_number") && (
                <p className="text-sm text-red-500">
                  {getErrorMessage("required_document_number")}
                </p>
              )}
            </div>

            <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
              <Label>{t("documentVerification.issuingDate")}</Label>
              <DatePicker
                disabled={loading}
                className={`w-full ${hasError("required_issuing_date") || hasError("invalid_issuing_date") ? "border-red-500" : ""}`}
                value={data?.issuingDate ?? undefined}
                onChange={(date) => handleChange("issuingDate", date)}
                placeholder={t(
                  "documentVerification.placeholders.datePlaceholder",
                )}
              />
              {hasError("required_issuing_date") && (
                <p className="text-sm text-red-500">
                  {getErrorMessage("required_issuing_date")}
                </p>
              )}
              {hasError("invalid_issuing_date") && (
                <p className="text-sm text-red-500">
                  {getErrorMessage("invalid_issuing_date")}
                </p>
              )}
            </div>
            <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
              <Label>{t("documentVerification.expirationDate")}</Label>
              <DatePicker
                disabled={loading}
                minDate={new Date()}
                className={`w-full ${hasError("required_expiration_date") || hasError("invalid_expiration_date") ? "border-red-500" : ""}`}
                value={data?.expirationDate ?? undefined}
                onChange={(date) => {
                  handleChange("expirationDate", date);
                }}
                placeholder={t(
                  "documentVerification.placeholders.datePlaceholder",
                )}
              />
              {hasError("required_expiration_date") && (
                <p className="text-sm text-red-500">
                  {getErrorMessage("required_expiration_date")}
                </p>
              )}
              {hasError("invalid_expiration_date") && (
                <p className="text-sm text-red-500">
                  {getErrorMessage("invalid_expiration_date")}
                </p>
              )}
            </div>

            <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
              <Label>{t("documentVerification.issuingAuthority")}</Label>
              <Select
                disabled={loading}
                onValueChange={(v) => handleChange("issuingAuthority", v)}
                value={data.issuingAuthority?.toString()}
              >
                <SelectTrigger
                  className={`w-full ${hasError("required_issuing_authority") ? "border-red-500" : ""}`}
                >
                  <SelectValue
                    placeholder={t(
                      "documentVerification.placeholders.selectIssuingAuthority",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Carabinieri">
                    {t("documentVerification.issuingAuthorities.Carabinieri")}
                  </SelectItem>
                  <SelectItem value="Consolato">
                    {t("documentVerification.issuingAuthorities.Consolato")}
                  </SelectItem>
                  <SelectItem value="Comune">
                    {t("documentVerification.issuingAuthorities.Comune")}
                  </SelectItem>
                  <SelectItem value="Ministero">
                    {t("documentVerification.issuingAuthorities.Ministero")}
                  </SelectItem>
                  <SelectItem value="Motorizzazione">
                    {t(
                      "documentVerification.issuingAuthorities.Motorizzazione",
                    )}
                  </SelectItem>
                  <SelectItem value="Prefettura">
                    {t("documentVerification.issuingAuthorities.Prefettura")}
                  </SelectItem>
                  <SelectItem value="Polizia">
                    {t("documentVerification.issuingAuthorities.Polizia")}
                  </SelectItem>
                  <SelectItem value="Questura">
                    {t("documentVerification.issuingAuthorities.Questura")}
                  </SelectItem>
                  <SelectItem value="Altro">
                    {t("documentVerification.issuingAuthorities.Altro")}
                  </SelectItem>
                </SelectContent>
              </Select>
              {hasError("required_issuing_authority") && (
                <p className="text-sm text-red-500">
                  {getErrorMessage("required_issuing_authority")}
                </p>
              )}
            </div>

            <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
              <Label>{t("documentVerification.issuingCountry")}</Label>
              <div
                className={
                  hasError("required_issuing_country")
                    ? "border border-red-500 rounded-md"
                    : ""
                }
              >
                <CountrySelect
                  value={data.issuingCountry}
                  onValueChange={(v) => handleChange("issuingCountry", v)}
                  disabled={loading}
                  apiEndpoint={`${environment.ELITE_BASE_URI}/geo/countries`}
                />
              </div>
              {hasError("required_issuing_country") && (
                <p className="text-sm text-red-500">
                  {getErrorMessage("required_issuing_country")}
                </p>
              )}
            </div>

            {data.issuingCountry?.iso2 === "IT" && (
              <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
                <Label>{t("documentVerification.issuingLocation")}</Label>
                <div
                  className={
                    hasError("required_issuing_city")
                      ? "border border-red-500 rounded-md"
                      : ""
                  }
                >
                  <CitySelect
                    value={data.issuingCity}
                    onValueChange={(v) => handleChange("issuingCity", v)}
                    disabled={loading}
                    apiEndpoint={`${environment.ELITE_BASE_URI}/geo/cities`}
                    nationId={data.issuingCountry?.id}
                  />
                </div>
                {hasError("required_issuing_city") && (
                  <p className="text-sm text-red-500">
                    {getErrorMessage("required_issuing_city")}
                  </p>
                )}
              </div>
            )}

            <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
              <Label>{t("documentVerification.documentType")}</Label>
              <Select
                disabled={loading}
                onValueChange={(v) => handleChange("documentType", v)}
                value={data.documentType}
              >
                <SelectTrigger
                  className={`w-full ${hasError("required_document_type") || hasError("invalid_document_type") ? "border-red-500" : ""}`}
                >
                  <SelectValue
                    placeholder={t(
                      "documentVerification.placeholders.selectType",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    disabled={
                      !europeanCountriesIso2.includes(
                        data?.issuingCountry?.iso2 || "",
                      )
                    }
                    value="ID_CARD"
                  >
                    <IdCard />
                    Identity Card
                  </SelectItem>
                  <SelectItem value="PASSPORT">
                    <Globe />
                    Passport
                  </SelectItem>
                  <SelectItem
                    disabled={data?.issuingCountry?.iso2 != "IT"}
                    value="DRIVER_LICENSE"
                  >
                    <CarFront />
                    Driver's License
                  </SelectItem>
                </SelectContent>
              </Select>
              {hasError("required_document_type") && (
                <p className="text-sm text-red-500">
                  {getErrorMessage("required_document_type")}
                </p>
              )}
              {hasError("invalid_document_type") && (
                <p className="text-sm text-red-500">
                  {getErrorMessage("invalid_document_type")}
                </p>
              )}
            </div>

            <div className="col-span-2 space-y-2">
              <Button
                className="w-full"
                variant="default"
                size={"lg"}
                disabled={loading || hasErrors}
                onClick={onNext}
              >
                {t("documentVerification.proceedToPersonalData")}
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
